/**
 * 体組成サマリ (Eufy snapshot) を表示する共通カード.
 *
 * Nutrition / Workout / Health Dashboard で再利用する。
 */

import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { LineChart as LineChartIcon, Minus, TrendingDown, TrendingUp } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { getEufyMeasurements, getEufySnapshot } from '@/api/services/eufy'
import { getBodyProfile } from '@/api/services/nutrition'
import type { EufyMeasurementItem, EufySnapshotResponse } from '@/api/types/eufy'
import type { BodyGender } from '@/api/types/nutrition'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PATHS } from '@/routes/paths'

// ==============================================================================
// FE-only Weight Projection
// ==============================================================================

const PROJECTION_MAX_DAYS = 180

function lerp(x: number, x0: number, x1: number, y0: number, y1: number): number {
  return y0 + ((x - x0) / (x1 - x0)) * (y1 - y0)
}

/** 体脂肪率に応じた週あたり減量率(%) */
function cutWeeklyRate(bodyFat: number | null, gender: BodyGender): number {
  if (bodyFat === null) return 0.7
  const bf = gender === 'female' ? bodyFat - 10 : bodyFat
  if (bf >= 30) return 1.0
  if (bf >= 25) return lerp(bf, 25, 30, 0.7, 1.0)
  if (bf >= 15) return 0.7
  if (bf >= 12) return lerp(bf, 12, 15, 0.5, 0.7)
  return 0.5
}

type ProjectionPoint = { date: string; weight_kg: number; body_fat: number | null }

function calculateProjection(startWeight: number, startBodyFat: number | null, gender: BodyGender): ProjectionPoint[] {
  let weight = startWeight
  let bodyFat = startBodyFat
  const lbm = bodyFat !== null ? weight * (1 - bodyFat / 100) : null
  const today = new Date()

  const points: ProjectionPoint[] = []

  for (let day = 0; day <= PROJECTION_MAX_DAYS; day++) {
    const d = new Date(today)
    d.setDate(d.getDate() + day)
    const dateStr = format(d, 'yyyy-MM-dd')

    // 7日おき + 初日 + 最終日
    if (day === 0 || day % 7 === 0 || day === PROJECTION_MAX_DAYS) {
      points.push({
        date: dateStr,
        weight_kg: Math.round(weight * 100) / 100,
        body_fat: bodyFat !== null ? Math.round(bodyFat * 10) / 10 : null,
      })
    }

    // cut_rate に基づく日次減量
    const rate = cutWeeklyRate(bodyFat, gender)
    const dailyLossKg = (weight * rate) / 100 / 7
    weight -= dailyLossKg

    // 体脂肪率の再計算 (LBM一定)
    if (lbm !== null && weight > 0) {
      bodyFat = Math.max(0, ((weight - lbm) / weight) * 100)
    }
  }

  return points
}

const fmtNumber = (v: number | null | undefined, digits = 1, suffix = ''): string => {
  if (v === null || v === undefined) return '—'
  return `${v.toFixed(digits)}${suffix}`
}

// ==============================================================================
// 体重トレンド判定 (減量 / 増量 / 維持)
// ==============================================================================

const TREND_DAYS = 7
const DAY_MS = 1000 * 60 * 60 * 24
/** この値(kg/週)未満の傾きは「維持」とみなす */
const MAINTAIN_THRESHOLD = 0.1

type TrendDirection = 'cut' | 'bulk' | 'maintain'

type WeightTrend = {
  direction: TrendDirection
  slopePerWeek: number
  points: { date: string; actual: number; trend: number }[]
}

/** 最小二乗法で傾き(kg/日)と切片を求める。x は経過日数。 */
function linearFit(points: { x: number; y: number }[]): { slope: number; intercept: number } | null {
  const n = points.length
  if (n < 2) return null
  let sumX = 0
  let sumY = 0
  let sumXY = 0
  let sumXX = 0
  for (const p of points) {
    sumX += p.x
    sumY += p.y
    sumXY += p.x * p.y
    sumXX += p.x * p.x
  }
  const denom = n * sumXX - sumX * sumX
  if (denom === 0) return null
  const slope = (n * sumXY - sumX * sumY) / denom
  const intercept = (sumY - slope * sumX) / n
  return { slope, intercept }
}

function computeWeightTrend(items: EufyMeasurementItem[], asOf: Date): WeightTrend | null {
  const asOfMs = asOf.getTime()
  const cutoffMs = asOfMs - TREND_DAYS * DAY_MS
  // 体重あり & 測定日時あり & asOf 基準で直近 TREND_DAYS 日以内、古い順
  const valid = items
    .filter((m): m is EufyMeasurementItem & { weight_kg: number; measured_at: string } => {
      if (m.weight_kg === null || m.measured_at === null) return false
      const t = new Date(m.measured_at).getTime()
      return t <= asOfMs && t >= cutoffMs
    })
    .sort((a, b) => new Date(a.measured_at).getTime() - new Date(b.measured_at).getTime())

  if (valid.length < 2) return null

  const t0 = new Date(valid[0].measured_at).getTime()
  const regression = valid.map((m) => ({
    x: (new Date(m.measured_at).getTime() - t0) / DAY_MS,
    y: m.weight_kg,
  }))

  const fit = linearFit(regression)
  if (fit === null) return null

  const slopePerWeek = fit.slope * 7
  const direction: TrendDirection =
    Math.abs(slopePerWeek) < MAINTAIN_THRESHOLD ? 'maintain' : slopePerWeek < 0 ? 'cut' : 'bulk'

  return {
    direction,
    slopePerWeek,
    points: regression.map((p, i) => ({
      date: valid[i].measured_at,
      actual: p.y,
      trend: fit.slope * p.x + fit.intercept,
    })),
  }
}

const TREND_STYLE: Record<TrendDirection, { label: string; color: string; Icon: typeof TrendingDown }> = {
  cut: { label: '減量傾向', color: 'hsl(221 83% 53%)', Icon: TrendingDown },
  bulk: { label: '増量傾向', color: 'hsl(25 95% 53%)', Icon: TrendingUp },
  maintain: { label: '維持', color: 'var(--muted-foreground)', Icon: Minus },
}

const WeightTrendSection = ({ trend }: { trend: WeightTrend }) => {
  const { label, color, Icon } = TREND_STYLE[trend.direction]
  const sign = trend.slopePerWeek > 0 ? '+' : ''
  const rateLabel = `${sign}${trend.slopePerWeek.toFixed(2)} kg/週`

  return (
    <div className="flex items-center justify-center gap-3 border-t">
      <div className="flex shrink-0 flex-col items-start gap-1">
        <div
          className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
          style={{ color, backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)` }}
        >
          <Icon className="size-3.5" />
          <span>{label}</span>
        </div>
        <span className="font-mono text-xs" style={{ color }}>
          {rateLabel}
        </span>
      </div>
      <div className="aspect-square w-24 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trend.points} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <YAxis hide domain={['dataMin - 0.3', 'dataMax + 0.3']} />
            {/* 実測値: 点のみ表示 */}
            <Line
              type="linear"
              dataKey="actual"
              stroke="transparent"
              dot={{ r: 2, fill: color, fillOpacity: 0.35 }}
              isAnimationActive={false}
            />
            {/* トレンド回帰直線 */}
            <Line
              type="linear"
              dataKey="trend"
              stroke={color}
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

type Props = {
  customerId?: string
  title?: string
  className?: string
  /** 基準日 (yyyy-MM-dd)。この日以前で最も近い測定を参照する。未指定時は今日(=最新) */
  date?: string
}

export const BodyMetricsSummary = ({ customerId, title = '体組成 (Eufy)', className, date }: Props) => {
  const [snapshot, setSnapshot] = useState<EufySnapshotResponse | null>(null)
  const [measurements, setMeasurements] = useState<EufyMeasurementItem[]>([])
  const [loading, setLoading] = useState(true)
  const [gender, setGender] = useState<BodyGender>('male')
  const navigate = useNavigate()

  // 基準日時 (この日時以前で最も近い測定を参照)
  const asOf = useMemo(() => (date ? new Date(`${date}T23:59:59`) : new Date()), [date])

  // nickname など date 非依存の補助情報用
  useEffect(() => {
    let cancelled = false
    getEufySnapshot(customerId)
      .then((s) => {
        if (!cancelled) setSnapshot(s)
      })
      .catch(() => {
        if (!cancelled) setSnapshot(null)
      })
    return () => {
      cancelled = true
    }
  }, [customerId])

  // asOf 以前の測定を新しい順で取得 (latest と trend の両方を導出)
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const end = date ? `${date}T23:59:59` : undefined
    getEufyMeasurements({ customer_id: customerId, end })
      .then((res) => {
        if (!cancelled) setMeasurements(res.items)
      })
      .catch(() => {
        if (!cancelled) setMeasurements([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [customerId, date])

  useEffect(() => {
    getBodyProfile()
      .then((p) => setGender(p.gender))
      .catch(() => null)
  }, [])

  // asOf 以前で最も近い測定
  const latestRow = useMemo(() => {
    const asOfMs = asOf.getTime()
    const sorted = measurements
      .filter((m): m is EufyMeasurementItem & { measured_at: string } => {
        return m.measured_at !== null && new Date(m.measured_at).getTime() <= asOfMs
      })
      .sort((a, b) => new Date(b.measured_at).getTime() - new Date(a.measured_at).getTime())
    return sorted[0] ?? null
  }, [measurements, asOf])

  // asOf 基準・直近7日の体重トレンド (減量/増量/維持)
  const weightTrend = useMemo(() => computeWeightTrend(measurements, asOf), [measurements, asOf])

  // FE-only projection (180日分)
  const projectionPoints = useMemo(() => {
    if (!latestRow?.weight_kg) return null
    const points = calculateProjection(latestRow.weight_kg, latestRow.body_fat, gender)
    return points.length >= 2 ? points : null
  }, [latestRow, gender])

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{title}</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground text-xs">読み込み中…</CardContent>
      </Card>
    )
  }

  if (!latestRow) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{title}</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground text-xs">
          体組成データがありません。Eufy 連携を確認してください。
        </CardContent>
      </Card>
    )
  }

  const latest = latestRow
  const measuredDate = new Date(latest.measured_at).toLocaleDateString('ja-JP')
  const isStale = new Date(latest.measured_at).getTime() < asOf.getTime() - 7 * DAY_MS

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-sm">
          <span>
            {title}
            {snapshot?.customer_nickname ? ` - ${snapshot.customer_nickname}` : ''}
          </span>
          <span className="text-muted-foreground text-xs font-normal">
            {measuredDate}
            {isStale ? ' (要更新)' : ''}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">体重</span>
            <span className="font-mono">{fmtNumber(latest.weight_kg, 1, 'kg')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">体脂肪率</span>
            <span className="font-mono">{fmtNumber(latest.body_fat, 1, '%')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">筋肉量</span>
            <span className="font-mono">{fmtNumber(latest.muscle_mass, 1, 'kg')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">骨格筋量</span>
            <span className="font-mono">{fmtNumber(latest.skeletal_muscle_mass, 1, 'kg')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">BMI</span>
            <span className="font-mono">{fmtNumber(latest.bmi, 1)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">基礎代謝</span>
            <span className="font-mono">{latest.bmr !== null ? `${Math.round(latest.bmr)}kcal` : '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">内臓脂肪Lv</span>
            <span className="font-mono">{fmtNumber(latest.visceral_fat, 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">体年齢</span>
            <span className="font-mono">{latest.body_age !== null ? `${latest.body_age}歳` : '—'}</span>
          </div>
        </div>
        {weightTrend && <WeightTrendSection trend={weightTrend} />}

        {projectionPoints && (
          <div className="space-y-2 border-t">
            <span className="text-xs font-medium">体重予測 (180日)</span>
            <div className="h-32 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={projectionPoints}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.3} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v: string) => format(new Date(v), 'M/d', { locale: ja })}
                    fontSize={10}
                  />
                  <YAxis yAxisId="weight" fontSize={10} domain={['auto', 'auto']} />
                  <YAxis yAxisId="bf" orientation="right" fontSize={10} domain={['auto', 'auto']} unit="%" />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      backgroundColor: 'hsl(240 10% 10%)',
                      border: '1px solid hsl(240 10% 25%)',
                      borderRadius: 6,
                      color: 'hsl(0 0% 95%)',
                    }}
                    labelStyle={{ color: 'hsl(0 0% 70%)', marginBottom: 4 }}
                    labelFormatter={(label) =>
                      typeof label === 'string' ? format(new Date(label), 'yyyy/M/d', { locale: ja }) : ''
                    }
                    formatter={(value, name) => {
                      const v = Number(value)
                      if (name === 'body_fat') return [`${v.toFixed(1)}%`, '体脂肪率']
                      return [`${v.toFixed(1)} kg`, '体重']
                    }}
                  />
                  <Line
                    yAxisId="weight"
                    type="monotone"
                    dataKey="weight_kg"
                    stroke="hsl(221 83% 53%)"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    dot={false}
                    connectNulls
                  />
                  <Line
                    yAxisId="bf"
                    type="monotone"
                    dataKey="body_fat"
                    stroke="hsl(25 95% 53%)"
                    strokeWidth={1.5}
                    strokeDasharray="4 2"
                    dot={false}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <Button variant="outline" size="sm" className="w-full" onClick={() => navigate(PATHS.EUFY.HOME)}>
          <LineChartIcon className="mr-1.5 size-3.5" />
          体重計グラフを見る
        </Button>
      </CardContent>
    </Card>
  )
}
